import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, ScanSearch } from "lucide-react";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";
import { GhostScan } from "@/components/Ghost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoSnapshot() {
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetch(`${API}/snapshot/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  const run = async (e) => {
    e.preventDefault();
    if (streaming) return;
    setStreaming(true);
    setError("");
    setResult("");
    setDone(false);
    try {
      const res = await fetch(`${API}/seo-snapshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Snapshot failed");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done: d, value } = await reader.read();
        if (d) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const payload = JSON.parse(p.slice(5).trim());
          if (payload.d) setResult((r) => r + payload.d);
          if (payload.error) setError(payload.error);
          if (payload.done) {
            setDone(true);
            setCount((c) => (c === null ? c : c + 1));
            toast.success("Snapshot delivered — a copy is on its way to your inbox.");
          }
        }
      }
    } catch (err) {
      setError(err.message || "Snapshot failed. Please try again.");
    } finally {
      setStreaming(false);
    }
  };

  const lines = result
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const verdict = lines[0] || "";
  const rest = lines.slice(1).join("\n");

  return (
    <section
      id="snapshot"
      data-testid="snapshot-section"
      className="relative overflow-hidden border-t border-slate-200 bg-gradient-to-b from-white via-[#F8F9FA] to-[#F1F3F5] py-24 md:py-32"
    >
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
      <GhostScan className="-bottom-24 -left-24 text-slate-900/[0.07]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <FadeUp>
              <Eyebrow>Free tool</Eyebrow>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
                Is your Website bringing you{" "}
                <span className="text-flame">leads and calls?</span>
              </h2>
              <p className="mt-6 max-w-md text-lg leading-relaxed text-mist md:text-xl">
                Drop in your web address and email, and get an instant,
                plain-language snapshot of what's likely holding your site
                back — powered by Gemini, delivered straight to your inbox.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <ul className="mt-8 space-y-3 text-lg text-slate-800">
                <li className="flex items-center gap-3">
                  <ScanSearch className="h-4 w-4 shrink-0 text-flame" />
                  A first-glance verdict on your online presence
                </li>
                <li className="flex items-center gap-3">
                  <ScanSearch className="h-4 w-4 shrink-0 text-flame" />
                  The usual suspects for sites like yours — and quick wins
                </li>
                <li className="flex items-center gap-3">
                  <ScanSearch className="h-4 w-4 shrink-0 text-flame" />
                  One concrete next step, in 60 seconds
                </li>
              </ul>
            </FadeUp>
          </div>

          <FadeUp delay={0.1}>
            <div
              data-testid="snapshot-widget"
              className="glass min-w-0 rounded-2xl border border-slate-200 p-7 md:p-9"
            >
              <form onSubmit={run} data-testid="snapshot-form">
                <label
                  htmlFor="snapshot-url"
                  className="font-mono text-sm font-medium uppercase tracking-[0.22em] text-slate-800"
                >
                  Your web address
                </label>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <input
                    id="snapshot-url"
                    data-testid="snapshot-url-input"
                    type="text"
                    required
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="yourbusiness.com"
                    className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-4 py-3.5 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
                  />
                  <button
                    data-testid="snapshot-submit-button"
                    type="submit"
                    disabled={streaming}
                    className="flex items-center justify-center gap-2 rounded-lg bg-flame px-6 py-3.5 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {streaming ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Reading
                      </>
                    ) : (
                      "Check my website"
                    )}
                  </button>
                </div>

                <label
                  htmlFor="snapshot-email"
                  className="mt-5 block font-mono text-sm font-medium uppercase tracking-[0.22em] text-slate-800"
                >
                  Your email
                  <span className="ml-2 normal-case tracking-normal text-faint">
                    — we send the snapshot here, that's all it's used for
                  </span>
                </label>
                <input
                  id="snapshot-email"
                  data-testid="snapshot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.com"
                  className="mt-3 w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3.5 text-lg text-slate-900 placeholder:text-slate-700 outline-none transition-colors focus:border-flame"
                />
              </form>

              <p
                data-testid="snapshot-counter"
                className="mt-4 flex items-center gap-2 font-mono text-sm text-faint"
              >
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
                {count === null
                  ? "Counting snapshots…"
                  : `${(1195 + count).toLocaleString()} free snapshots run`}
              </p>

              {error && (
                <div
                  data-testid="snapshot-error"
                  className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-base text-red-300"
                >
                  {error}
                </div>
              )}

              {(result || streaming) && !error && (
                <div
                  data-testid="snapshot-result"
                  className="mt-6 rounded-xl border border-slate-200 bg-white/70 p-5"
                >
                  {verdict && (
                    <p className="font-display text-lg font-bold leading-snug text-slate-900 sm:text-xl">
                      {verdict}
                    </p>
                  )}
                  {rest && (
                    <p className="mt-4 whitespace-pre-wrap text-lg leading-relaxed text-mist">
                      {rest}
                    </p>
                  )}
                  {streaming && (
                    <span className="mt-2 inline-block h-4 w-2 animate-pulse bg-flame" />
                  )}
                  {done && (
                    <button
                      data-testid="snapshot-cta"
                      onClick={() => scrollToId("book")}
                      className="group mt-5 flex items-center gap-2 rounded-2xl bg-flame px-5 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
                    >
                      Turn this into a plan — book a free call
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
